import React, {
  useMemo,
} from 'react';
import compact from 'lodash/compact';
import {
  US_STATES,
  USStateType,
} from 'core/constants';
import AutoComplete from 'core/components/AutoComplete';
import {
  isEmptyString,
} from '@/utils/misc';

type Props = {
  onChange: (...event: any[]) => void;
  onBlur: () => void;
  value?: string | null;
  className?: string;
  placement?: 'top' | 'bottom' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
  // If provided, move this state (by abbreviation or name) to the top of the options list
  priorityState?: string | null;
};

export default function USStateSelect({
  onChange,
  onBlur,
  value,
  className,
  priorityState,
}: Props) {
  const selectedState = useMemo<USStateType | null>(() => US_STATES.find(
    (region) => (
      region.name === value?.trim()
      || region.abbreviation === value?.trim()
    ),
  ) ?? null, [value]);

  const onStateValueChange = (val: USStateType | USStateType[]) => {
    const value = (val as USStateType)?.abbreviation ?? '';
    onChange(value);
  };

  const orderedOptions = useMemo<USStateType[]>(() => {
    const key = priorityState?.trim();
    const idx = !isEmptyString(key)
      ? US_STATES.findIndex(s => s.abbreviation === key)
      : -1;

    return idx > 0
      ? [US_STATES[idx], ...US_STATES.filter((_, i) => i !== idx)]
      : US_STATES;
  }, [priorityState]);

  return (
    <AutoComplete<USStateType>
      onChange={onStateValueChange}
      onBlur={onBlur}
      value={selectedState}
      className={className}
      placeholder="State"
      id="state"
      options={orderedOptions}
      getLabel={(region) => compact([region.abbreviation, region.name]).join(' - ')}
      isOptionEqualToValue={(option, selectedValue) => (
        typeof selectedValue !== 'string'
          && (option.name === selectedValue.name || option.abbreviation === selectedValue.abbreviation)
      )}
      classes={{
        inputRoot: '!pr-2 !py-2 !truncate',
        input: '!mr-4',
      }}
    />
  );
}
